export class NuxtPath {
  #path;

  /**
   * @param {string} path
   */
  constructor(path) {
    this.#validatePath(path);
    this.#path = path;
  }

  #validatePath(path) {
    if (typeof path !== "string") {
      throw new TypeError("NuxtPath must be a string, got " + typeof path);
    }
    if (!path.startsWith("/")) {
      throw new Error("NuxtPath must start with a '/'");
    }
  }

  isResolved() {
    return !this.#path.includes("/:");
  }

  get pathVariables() {
    const regex = /:(\w+)/g;
    const matches = [];
    let match;
    while ((match = regex.exec(this.#path)) !== null) {
      matches.push(match[1]);
    }
    return matches;
  }

  hasPlaceholder(placeholder) {
    if (typeof placeholder !== "string" || placeholder.trim() === "") {
      throw new TypeError("Placeholder must non-empty be a string");
    }
    const placeHolderStartIndex = this.#path.indexOf(`:${placeholder}`);
    if (placeHolderStartIndex === -1) {
      return false;
    }
    const placeHolderEndIndex = placeHolderStartIndex + placeholder.length + 1; // +1 for the ':'
    // Check if the next character is a valid path character (not a placeholder)
    const nextChar = this.#path[placeHolderEndIndex];
    return nextChar === "/" || nextChar === "" || nextChar === undefined;
  }

  /**
   *
   * @param {{ [key: string]: string }} data key-value pairs where keys correspond to placeholders in the nuxtPath and values are the replacements.
   * @returns {NuxtPath} The resolved path with placeholders replaced by their corresponding values.
   * @throws {Error} If any placeholder in the nuxtPath is not found in the data object or if there are unresolved placeholders left in the path.
   */
  resolve(data) {
    if (typeof data !== "object" || data === null) {
      throw new TypeError("data must be an object");
    }

    let resolved = this.#path;

    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null) {
        throw new TypeError(`Key ${key} is not defined in the data object`);
      }
      if (!this.hasPlaceholder(key)) {
        throw new Error(
          `Key '${key}' is not defined in nuxtPath: ${this.#path}`
        );
      }
      resolved = resolved.replace(`:${key}`, value);
    }

    if (resolved.includes("/:")) {
      throw new Error(
        `The nuxtPath ${
          this.#path
        } contains unresolved placeholders: ${resolved}`
      );
    }

    return new NuxtPath(resolved);
  }

  get path() {
    return this.#path;
  }
}
